import { Injectable, inject } from '@angular/core';
import { Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { FormKeys, TaxableAnnualIncome } from '@nx-customer-apps/shared/enums';
import { BuildingType } from '@nx-customer-apps/components';
import { FormOptions, QuestionOverride } from '@nx-customer-apps/shared/interfaces';
import { CommonFormService } from '../../services/common-form.service';
import {
    RadioQuestion,
    Question,
    TextboxQuestion,
    StepperQuestion,
    taxableAnnualIncomeToPhraseKeyDictionary
} from '@nx-customer-apps/shared/utils';
import { FormToolsService, LocaleService, ValidatorsService } from '../../services';
import { DEFAULT_MINOR_CHILDREN } from '../../shared/utils';

@Injectable({
    providedIn: 'root'
})
export class HeatPumpIntermediaryFormService {
    private commonFormService = inject(CommonFormService);
    private validatorsService = inject(ValidatorsService);
    private formToolsService = inject(FormToolsService);
    private translateService = inject(TranslateService);
    private localeService = inject(LocaleService);

    private getDevice(override?: QuestionOverride) {
        return new RadioQuestion({
            key: FormKeys.IsOldDeviceUsed,
            question: 'HEAT_PUMP_INTERMEDIARY.DEVICE.DEVICE.TITLE',
            styles: 'ca-radio-columns',
            value: override?.value,
            defaultErrorMessage: 'HEAT_PUMP_INTERMEDIARY.DEVICE.DEVICE.ERRORS.REQUIRED',
            validators: [Validators.required],
            options: [
                { key: 'HEAT_PUMP_INTERMEDIARY.DEVICE.DEVICE.YES', value: true },
                { key: 'HEAT_PUMP_INTERMEDIARY.DEVICE.DEVICE.NO', value: false }
            ]
        });
    }

    private getTaxableAnnualIncome(override?: QuestionOverride) {
        const thousandSeparator = this.localeService.getThousandSeparator();
        const currencySymbol = this.localeService.getCurrencySymbol();
        const translationOptions = { thousandSeparator, currencySymbol };
        return new RadioQuestion({
            key: FormKeys.TaxableAnnualIncome,
            question: 'HEAT_PUMP_INTERMEDIARY.HOUSEHOLD.INCOME.TITLE_V2',
            styles: 'ca-radio-columns',
            defaultErrorMessage: 'HEAT_PUMP_INTERMEDIARY.HOUSEHOLD.INCOME.ERRORS.REQUIRED',
            validators: [Validators.required],
            value: override?.value,
            options: [
                {
                    key: this.translateService.instant(
                        taxableAnnualIncomeToPhraseKeyDictionary[TaxableAnnualIncome.UpTo30k],
                        translationOptions
                    ),
                    value: TaxableAnnualIncome.UpTo30k
                },
                {
                    key: this.translateService.instant(
                        taxableAnnualIncomeToPhraseKeyDictionary[TaxableAnnualIncome.UpTo40k],
                        translationOptions
                    ),
                    value: TaxableAnnualIncome.UpTo40k
                },
                {
                    key: this.translateService.instant(
                        taxableAnnualIncomeToPhraseKeyDictionary[TaxableAnnualIncome.UpTo50k],
                        translationOptions
                    ),
                    value: TaxableAnnualIncome.UpTo50k
                },
                {
                    key: this.translateService.instant(
                        taxableAnnualIncomeToPhraseKeyDictionary[TaxableAnnualIncome.UpTo60k],
                        translationOptions
                    ),
                    value: TaxableAnnualIncome.UpTo60k
                },
                {
                    key: this.translateService.instant(
                        taxableAnnualIncomeToPhraseKeyDictionary[TaxableAnnualIncome.Over60k],
                        translationOptions
                    ),
                    value: TaxableAnnualIncome.Over60k
                }
            ]
        });
    }

    private getNumberOfMinorChildren(override?: QuestionOverride) {
        return new StepperQuestion({
            key: FormKeys.NumberOfMinorChildren,
            question: 'HEAT_PUMP_INTERMEDIARY.HOUSEHOLD.CHILDREN.TITLE',
            label: 'HEAT_PUMP_INTERMEDIARY.HOUSEHOLD.CHILDREN.LABEL',
            validators: [Validators.required],
            value: override?.value ?? DEFAULT_MINOR_CHILDREN,
            min: 0,
            max: 14,
            step: 1
        });
    }

    private getOwnerOfTheBuilding(override?: QuestionOverride) {
        return new RadioQuestion({
            key: FormKeys.IsOwnerOfTheBuilding,
            question: 'HEAT_PUMP_INTERMEDIARY.HOUSEHOLD.BUILDING_OWNER.TITLE',
            styles: 'ca-radio-columns',
            defaultErrorMessage: 'HEAT_PUMP_INTERMEDIARY.HOUSEHOLD.BUILDING_OWNER.ERRORS.REQUIRED',
            validators: [Validators.required],
            value: override?.value,
            options: [
                { key: 'HEAT_PUMP_INTERMEDIARY.HOUSEHOLD.YES', value: true },
                { key: 'HEAT_PUMP_INTERMEDIARY.HOUSEHOLD.NO', value: false }
            ]
        });
    }

    private getOwnerMainResidence(override?: QuestionOverride) {
        return new RadioQuestion({
            key: FormKeys.IsOwnerMainResidence,
            question: 'HEAT_PUMP_INTERMEDIARY.HOUSEHOLD.MAIN_RESIDENCE.TITLE',
            styles: 'ca-radio-columns',
            defaultErrorMessage: 'HEAT_PUMP_INTERMEDIARY.HOUSEHOLD.MAIN_RESIDENCE.ERRORS.REQUIRED',
            validators: [Validators.required],
            value: override?.value,
            options: [
                { key: 'HEAT_PUMP_INTERMEDIARY.HOUSEHOLD.YES', value: true },
                { key: 'HEAT_PUMP_INTERMEDIARY.HOUSEHOLD.NO', value: false }
            ]
        });
    }

    private getOldHeatingSystemDefective(override?: QuestionOverride) {
        return new RadioQuestion({
            key: FormKeys.IsOldHeatingSystemDefective,
            question: 'HEAT_PUMP_INTERMEDIARY.HOUSEHOLD.DEFECTIVE.TITLE',
            styles: 'ca-radio-columns',
            defaultErrorMessage: 'HEAT_PUMP_INTERMEDIARY.HOUSEHOLD.DEFECTIVE.ERRORS.REQUIRED',
            validators: [Validators.required],
            value: override?.value,
            options: [
                { key: 'HEAT_PUMP_INTERMEDIARY.HOUSEHOLD.YES', value: true },
                { key: 'HEAT_PUMP_INTERMEDIARY.HOUSEHOLD.NO', value: false }
            ]
        });
    }

    private getResidentialUnits(override?: QuestionOverride) {
        return new TextboxQuestion({
            question: 'HEAT_PUMP_INTERMEDIARY.SUBSIDY.RESIDENTIAL_UNITS.TITLE',
            description: 'HEAT_PUMP_INTERMEDIARY.SUBSIDY.RESIDENTIAL_UNITS.DESCRIPTION',
            defaultErrorMessage: 'HEAT_PUMP_INTERMEDIARY.SUBSIDY.RESIDENTIAL_UNITS.REQUIRED',
            label: 'HEAT_PUMP_INTERMEDIARY.SUBSIDY.RESIDENTIAL_UNITS.LABEL',
            value: override?.value,
            key: FormKeys.NumberOfResidentialUnits,
            hints: [this.formToolsService.getNumericHint()],
            validators: [Validators.required, this.validatorsService.minMaxValidator({ min: 2, max: 500 })]
        });
    }

    public getDeviceQuestions(options?: FormOptions): RadioQuestion[] {
        const deviceOverride = options?.questions?.[FormKeys.IsOldDeviceUsed];
        const device = this.getDevice(deviceOverride);

        return [device];
    }

    public getBuildingQuestions(options?: FormOptions): Question[] {
        const buildingConstructionYear = this.commonFormService.getBuildingConstructionYear(options);
        return [buildingConstructionYear];
    }

    public getSubsidyEligibilityQuestions(options?: FormOptions): Question[] {
        // Multi family house
        if (options?.buildingType === BuildingType.MultiFamilyHouse) {
            const residentialUnitsOverride = options?.questions?.[FormKeys.NumberOfResidentialUnits];
            const residentialUnits = this.getResidentialUnits(residentialUnitsOverride);
            return [residentialUnits];
        }

        // Single family house
        const taxableAnnualIncomeOverride = options?.questions?.[FormKeys.TaxableAnnualIncome];
        const numberOfMinorChildrenOverride = options?.questions?.[FormKeys.NumberOfMinorChildren];
        const ownerOfTheBuildingOverride = options?.questions?.[FormKeys.IsOwnerOfTheBuilding];
        const ownerMainResidenceOverride = options?.questions?.[FormKeys.IsOwnerMainResidence];
        const taxableAnnualIncome = this.getTaxableAnnualIncome(taxableAnnualIncomeOverride);
        const numberOfMinorChildren = this.getNumberOfMinorChildren(numberOfMinorChildrenOverride);
        const ownerOfTheBuilding = this.getOwnerOfTheBuilding(ownerOfTheBuildingOverride);
        const ownerMainResidence = this.getOwnerMainResidence(ownerMainResidenceOverride);

        const oldHeatingSystemDefectiveOverride = options?.questions?.[FormKeys.IsOldHeatingSystemDefective];
        if (!oldHeatingSystemDefectiveOverride) {
            return [taxableAnnualIncome, ownerOfTheBuilding, ownerMainResidence, numberOfMinorChildren];
        }

        const oldHeatingSystemDefective = this.getOldHeatingSystemDefective(oldHeatingSystemDefectiveOverride);

        return [taxableAnnualIncome, ownerOfTheBuilding, ownerMainResidence, numberOfMinorChildren, oldHeatingSystemDefective];
    }
}
